'use client'

import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  FiActivity,
  FiAlertCircle,
  FiArrowUpRight,
  FiBarChart2,
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiHome,
  FiKey,
  FiMessageCircle,
  FiTrendingUp,
  FiUser,
  FiZap
} from 'react-icons/fi'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@/lib/utils'

type RangeKey = '7d' | 'current_month' | 'last_month' | '90d'

const RANGE_OPTIONS: Array<{ value: RangeKey; label: string }> = [
  { value: '7d', label: 'Últimos 7 días' },
  { value: 'current_month', label: 'Este mes' },
  { value: 'last_month', label: 'Mes anterior' },
  { value: '90d', label: 'Últimos 3 meses' }
]

function getAlertTone(summary: any) {
  if (summary?.estadoPlan === 'Vencido' || summary?.estadoPlan === 'Suspendido') {
    return 'border-red-200 bg-red-50 text-red-700'
  }
  if (summary?.porcentajeConsumido >= 80) {
    return 'border-amber-200 bg-amber-50 text-amber-700'
  }
  return 'border-blue-200 bg-blue-50 text-blue-700'
}

export default function ResumenPage() {
  const [summary, setSummary] = useState<any>(null)
  const [chartData, setChartData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [usuario, setUsuario] = useState<any>(null)
  const [selectedRange, setSelectedRange] = useState<RangeKey>('current_month')
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  useEffect(() => {
    fetchData()
  }, [selectedRange, selectedMonth, selectedYear])

  const fetchData = async () => {
    setLoading(true)
    try {
      const queryParams = `?range=${selectedRange}&month=${selectedMonth}&year=${selectedYear}`
      const [uRes, sRes, cRes] = await Promise.all([
        fetch('/api/auth/verificar').then((r) => r.json()),
        fetch(`/api/user/summary${queryParams}`).then((r) => r.json()),
        fetch(`/api/user/chart${queryParams}`).then((r) => r.json())
      ])

      if (uRes.success) setUsuario(uRes.usuario)
      if (sRes.success) setSummary(sRes.data)
      if (cRes.success) setChartData(cRes.data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const periodLabel = useMemo(() => {
    const found = RANGE_OPTIONS.find((option) => option.value === selectedRange)
    return found?.label || 'Este mes'
  }, [selectedRange])

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    )
  }

  const integraciones = summary?.integraciones || {}
  const consultasRestantes = summary?.consultasRestantes
  const porcentajeConsumido = summary?.porcentajeConsumido || 0
  const alertTone = getAlertTone(summary)

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-gray-900">Mi Resumen</h1>
        <p className="text-gray-500">Estado actual de tu plan, consumo e integraciones principales.</p>
      </div>

      {(summary?.mensajeLimite || summary?.recomendacion) && (
        <div className={cn('rounded-3xl border px-5 py-4 shadow-sm', alertTone)}>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 rounded-2xl bg-white/70 p-2">
              <FiAlertCircle className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-bold uppercase tracking-[0.2em]">Atención de cuenta</p>
              {summary?.mensajeLimite ? <p className="text-sm font-semibold">{summary.mensajeLimite}</p> : null}
              {summary?.recomendacion ? <p className="text-sm">{summary.recomendacion}</p> : null}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <Card className="overflow-hidden border border-gray-100 bg-white shadow-sm lg:col-span-1">
          <CardContent className="flex flex-col items-center pt-8 pb-6 text-center">
            <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-blue-50">
              <FiUser className="h-10 w-10 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">{usuario?.nombreCompleto || 'Usuario'}</h3>
            <p className="mt-1 text-sm text-gray-500">@{usuario?.nombreUsuario || 'usuario'}</p>

            <div className="mt-6 w-full space-y-3">
              <div className="rounded-2xl border border-gray-100 bg-slate-50 px-4 py-3 text-left">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-gray-400">Plan actual</span>
                <span className="text-sm font-bold text-blue-700">{summary?.nombrePlan || 'Básico'}</span>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-slate-50 px-4 py-3 text-left">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-gray-400">Próxima renovación</span>
                <span className="text-sm font-semibold text-gray-800">
                  {summary?.fechaRenovacion ? format(new Date(summary.fechaRenovacion), 'dd/MM/yyyy') : 'Sin fecha'}
                </span>
                {typeof summary?.diasParaRenovacion === 'number' ? (
                  <p className="mt-1 text-xs text-gray-500">
                    {summary.diasParaRenovacion >= 0 ? `Faltan ${summary.diasParaRenovacion} días` : 'Renovación vencida'}
                  </p>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-6 lg:col-span-3">
          <Card className="overflow-hidden border-none bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white shadow-[0_24px_60px_rgba(15,23,42,0.22)]">
            <CardContent className="p-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-3">
                  <div className="inline-flex rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] text-blue-100">
                    Consumo actual
                  </div>
                  <div className="flex flex-wrap items-end gap-3">
                    <span className="text-4xl font-black">
                      {summary?.consultasRealizadas?.toLocaleString('es-PE') || 0}
                    </span>
                    <span className="pb-1 text-sm text-blue-100">
                      de {summary?.consultasMensuales === -1 ? 'ilimitadas' : summary?.consultasMensuales?.toLocaleString('es-PE')} consultas
                    </span>
                  </div>
                  <p className="max-w-2xl text-sm text-blue-100/90">
                    {consultasRestantes === null
                      ? 'Tu plan no tiene límite mensual de consultas.'
                      : `Te quedan ${consultasRestantes.toLocaleString('es-PE')} consultas disponibles en ${periodLabel.toLowerCase()}.`}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-100">Consumo</p>
                    <p className="mt-2 text-2xl font-bold">{porcentajeConsumido}%</p>
                  </div>
                  <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-100">Restante</p>
                    <p className="mt-2 text-2xl font-bold">{consultasRestantes === null ? '∞' : consultasRestantes.toLocaleString('es-PE')}</p>
                  </div>
                  <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-100">Promedio</p>
                    <p className="mt-2 text-2xl font-bold">{summary?.promedioDiario || 0}</p>
                  </div>
                  <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-100">Pico</p>
                    <p className="mt-2 text-2xl font-bold">{summary?.picoConsumo || 0}</p>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="h-3 overflow-hidden rounded-full bg-white/10">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all duration-500',
                      porcentajeConsumido >= 100 ? 'bg-red-400' : porcentajeConsumido >= 80 ? 'bg-amber-400' : 'bg-emerald-400'
                    )}
                    style={{ width: `${Math.min(porcentajeConsumido, 100)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              icon={<FiActivity className="h-6 w-6 text-purple-600" />}
              iconBg="bg-purple-50"
              title="Consumo del periodo"
              value={`${summary?.consultasRealizadas?.toLocaleString('es-PE') || 0}`}
              subtitle={periodLabel}
            />
            <MetricCard
              icon={<FiClock className="h-6 w-6 text-green-600" />}
              iconBg="bg-green-50"
              title="Renovación"
              value={summary?.fechaRenovacion ? format(new Date(summary.fechaRenovacion), 'dd/MM/yyyy') : 'N/A'}
              subtitle={typeof summary?.diasParaRenovacion === 'number' ? `${summary.diasParaRenovacion} días` : 'Sin programación'}
            />
            <MetricCard
              icon={<FiTrendingUp className="h-6 w-6 text-blue-600" />}
              iconBg="bg-blue-50"
              title="Comparación"
              value={`${summary?.deltaConsultas >= 0 ? '+' : ''}${summary?.deltaConsultas || 0}`}
              subtitle="vs. periodo anterior"
              valueClassName={summary?.deltaConsultas >= 0 ? 'text-blue-600' : 'text-emerald-600'}
            />
            <MetricCard
              icon={<FiCheckCircle className="h-6 w-6 text-cyan-600" />}
              iconBg="bg-cyan-50"
              title="Estado del plan"
              value={summary?.estadoPlan || 'Desconocido'}
              subtitle={summary?.recomendacion || 'Cuenta en seguimiento'}
              valueClassName={summary?.estadoPlan === 'Activo' ? 'text-green-600' : 'text-red-500'}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[2fr,1fr]">
        <Card className="border-none shadow-sm">
          <CardHeader className="flex flex-col gap-4 border-b bg-gray-50/60 pb-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <FiBarChart2 className="text-blue-500" /> Historial de Consultas
              </CardTitle>
              <p className="mt-1 text-sm text-gray-500">Lee mejor tu comportamiento y anticipa si necesitas escalar tu plan.</p>
            </div>

            <div className="flex flex-col gap-2 lg:items-end">
              <div className="flex flex-wrap gap-2">
                {RANGE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSelectedRange(option.value)}
                    className={cn(
                      'rounded-full px-3 py-2 text-xs font-bold transition-all',
                      selectedRange === option.value
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {selectedRange === 'current_month' && (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="rounded-xl bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 ring-1 ring-slate-200 focus:outline-none"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {format(new Date(2026, i, 1), 'MMMM', { locale: es })}
                      </option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="rounded-xl bg-white px-3 py-2 text-xs font-bold uppercase tracking-widest text-slate-600 ring-1 ring-slate-200 focus:outline-none"
                  >
                    {[2024, 2025, 2026].map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <MiniStat label="Periodo" value={periodLabel} />
              <MiniStat label="Total" value={`${summary?.consultasRealizadas || 0}`} />
              <MiniStat label="Promedio diario" value={`${summary?.promedioDiario || 0}`} />
              <MiniStat label="Pico" value={`${summary?.picoConsumo || 0}`} />
            </div>

            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorConsultas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.18} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} interval="preserveStartEnd" />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(15,23,42,0.08)' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="consultas"
                    stroke="#2563eb"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorConsultas)"
                    animationDuration={900}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="border-none shadow-sm">
            <CardHeader className="border-b bg-gray-50/60">
              <CardTitle className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <FiZap className="text-blue-500" /> Siguiente acción
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-5">
              <ActionLink href="/plan" label="Subir o revisar plan" icon={<FiTrendingUp className="h-4 w-4" />} />
              <ActionLink href="/token" label="Gestionar token API" icon={<FiKey className="h-4 w-4" />} />
              <ActionLink href="/whatsapp-api" label="Conectar WhatsApp" icon={<FiMessageCircle className="h-4 w-4" />} />
              <ActionLink href="/empresas" label="Configurar empresas" icon={<FiHome className="h-4 w-4" />} />
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm">
            <CardHeader className="border-b bg-gray-50/60">
              <CardTitle className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <FiBriefcase className="text-blue-500" /> Estado de Integraciones
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-5">
              <IntegrationRow label="Token API" active={Boolean(integraciones?.tokenApi?.activo)} detail={integraciones?.tokenApi?.ultimoUso ? `Último uso: ${format(new Date(integraciones.tokenApi.ultimoUso), 'dd/MM/yyyy')}` : 'Sin token activo'} />
              <IntegrationRow label="WhatsApp" active={Boolean(integraciones?.whatsapp?.activo)} detail={integraciones?.whatsapp?.activo ? 'Sesión conectada' : 'Pendiente de vincular'} />
              <IntegrationRow label="Empresas" active={(integraciones?.empresas?.activas || 0) > 0} detail={`${integraciones?.empresas?.activas || 0} registradas`} />
              <IntegrationRow label="SUNAT / Facturación" active={Boolean(integraciones?.sunat?.activo)} detail={integraciones?.sunat?.activo ? 'Disponible para operar' : 'Configura una empresa primero'} />
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="border-none shadow-sm overflow-hidden">
          <CardHeader className="border-b bg-gray-50/60">
            <CardTitle className="flex items-center gap-2 text-sm font-bold text-gray-700">
              <FiBriefcase className="text-blue-500" /> Resumen Operacional
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <OperationalItem label="Plan solicitado" value={summary?.nombrePlan || 'Sin plan'} />
              <OperationalItem label="Precio mensual" value={`S/ ${Number(summary?.precioPlan || 0).toFixed(2)}`} />
              <OperationalItem label="Consultas restantes" value={consultasRestantes === null ? 'Ilimitadas' : consultasRestantes.toLocaleString('es-PE')} />
              <OperationalItem label="Servicios activos" value={`${integraciones?.serviciosActivos || 0} activos`} />
              <OperationalItem label="Renovación" value={summary?.fechaRenovacion ? format(new Date(summary.fechaRenovacion), 'dd/MM/yyyy') : 'Sin fecha'} />
              <OperationalItem label="Registro" value={summary?.fechaRegistro ? format(new Date(summary.fechaRegistro), 'dd/MM/yyyy') : '---'} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm overflow-hidden">
          <CardHeader className="border-b bg-gray-50/60">
            <CardTitle className="flex items-center gap-2 text-sm font-bold text-gray-700">
              <FiCalendar className="text-blue-500" /> Actividad Reciente
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5">
            <div className="space-y-4">
              {(summary?.actividadReciente || []).length > 0 ? (
                summary.actividadReciente.map((item: any, index: number) => (
                  <div key={`${item.tipo}-${index}`} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="mt-0.5 rounded-2xl bg-white p-2 text-slate-600 shadow-sm">
                      {item.tipo === 'token' ? <FiKey className="h-4 w-4" /> : item.tipo === 'plan' ? <FiTrendingUp className="h-4 w-4" /> : item.tipo === 'renovacion' ? <FiClock className="h-4 w-4" /> : <FiAlertCircle className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{item.titulo}</p>
                      <p className="mt-1 text-sm text-slate-500">{item.descripcion}</p>
                    </div>
                    <div className="shrink-0 text-xs font-medium text-slate-400">
                      {item.fecha ? format(new Date(item.fecha), 'dd/MM/yyyy') : '---'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  Aún no hay actividad reciente para mostrar en este resumen.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function MetricCard({
  icon,
  iconBg,
  title,
  value,
  subtitle,
  valueClassName
}: {
  icon: ReactNode
  iconBg: string
  title: string
  value: string
  subtitle: string
  valueClassName?: string
}) {
  return (
    <Card className="border-none shadow-sm">
      <CardContent className="p-6">
        <div className={cn('mb-4 flex h-12 w-12 items-center justify-center rounded-2xl', iconBg)}>{icon}</div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{title}</p>
        <p className={cn('mt-3 text-2xl font-bold text-slate-900', valueClassName)}>{value}</p>
        <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
      </CardContent>
    </Card>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{label}</p>
      <p className="mt-2 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  )
}

function ActionLink({ href, label, icon }: { href: string; label: string; icon: ReactNode }) {
  return (
    <Link
      href={href}
      className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition-all hover:border-slate-200 hover:bg-white hover:shadow-sm"
    >
      <span className="flex items-center gap-3">
        <span className="text-slate-500">{icon}</span>
        {label}
      </span>
      <FiArrowUpRight className="h-4 w-4 text-slate-400" />
    </Link>
  )
}

function IntegrationRow({ label, active, detail }: { label: string; active: boolean; detail: string }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="mt-1 text-xs text-slate-500">{detail}</p>
      </div>
      <span className={cn('rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em]', active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600')}>
        {active ? 'Activo' : 'Pendiente'}
      </span>
    </div>
  )
}

function OperationalItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{label}</p>
      <p className="mt-2 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  )
}
